import { format } from 'date-fns';
import * as ordersStore from '@/shared/ordersStore';
import { useCallback, useMemo, useState } from 'react';
import { ClientProps } from '@/types';
import { OrderItemsProps } from '../orderStatusPage/useOrderStatusPage';
import { useFetchAxios, useGlobals, useNetworkStatusContext, useUserContext } from '@/hooks';

interface HookProps {
  order?: OrderItemsProps;
  client?: ClientProps;
  closeModal?: () => void;
  companyData?: {
    clientName: string;
    clientRefNumber: string;
    clientCnpj: string;
    clientUnilever: boolean;
  };
}

function useOrderStatusModal(props = {} as HookProps) {
  const { order, companyData, closeModal } = props;

  const { version } = useGlobals();
  const { axiosCtx } = useFetchAxios();
  const { customer } = useUserContext();
  const { connectivityStatus } = useNetworkStatusContext();

  const [loading, setLoading] = useState(false);

  const orderPayload = useMemo(() => {
    if (order) {
      const items = order?.actionsPending?.map((item) => ({
        cod_segmento: item?.cod_segmento,
        cod_produto: item?.cod_produto,
        unilever: item?.unilever ?? item?.UNILEVER,
        sku: item?.sku,
        product: item?.product,
        price: item?.price,
        unit_price: item?.unitPrice,
        unit_price_per_item: item?.unitPricePerItem,
        st_price: item?.stPrice,
        integration_price: item?.integrationPrice,
        quantity: item?.quantity,
        packaging: item?.packaging,
        st_price_packaging: item?.stPricePackaging,
        unit_price_flex: item?.unit_price_flex,
        integration_price_flex: item?.integrationPriceFlex || 0,
        unit_price_per_item_flex: item?.unit_price_per_item_flex,
        st_price_flex: item?.st_price_flex,
        price_flex: item?.price_flex,
        percentual_flex: item?.percentualFlex || '0',
        quantity_multiplo: item?.quantityMultiplo,
        status: '',
        reason: '',
      }));

      return {
        version,
        origin_function: 'reprocess',
        current_time: format(new Date(), 'yyyy-MM-dd HH:mm:ss'),
        cart_opened_at: order?.cart_opened_at,
        company: {
          unilever: companyData?.clientUnilever,
          name: companyData?.clientName,
          id: companyData?.clientRefNumber,
          cnpj: companyData?.clientCnpj,
          Cod_rca: customer?.codPessoa?.toString(),
        },
        orders: {
          order_number: order?.orderNumber,
          erp_order_number: order?.erpNumber,
          order_number_magento: order?.orderNumberMagento,
          origin: 'BateForte_web_mobile_rca',
          cod_loja: Number(order?.codLoja),
          cod_segmento_loja: Number(order?.codSegmentoLoja),
          items: items?.length,
          status: '',
          reason: '',
          order_date: order?.orderDate,
          total: order?.total,
          payment_terms: order?.paymentTerms,
          actions_pending: items,
        },
      };
    }
  }, [order, version, customer, companyData]);
  
  const handleReprocessOrder = useCallback(async () => {
    if (!orderPayload) {
      return;
    }

    if (connectivityStatus === 'online') {
      try {
        setLoading(true);
        
        const orderNumber = orderPayload?.orders?.order_number;
        if (orderNumber) {
          try {
            await ordersStore.markAcked(orderNumber);
          } catch {
          }
        }

        const ok = await axiosCtx
          .post('/integra-pedido', orderPayload)
          .then((res) => Boolean(res?.data?.message))
          .catch((error) => {
            console.error('Falha ao reenviar pedido:', error);
            return false;
          });

        if (ok) {
          await new Promise((resolve) => setTimeout(() => resolve(true), 1000));
          closeModal && closeModal();
          setLoading(false);
        }
      } catch (error) {
        setLoading(false);
      }
    }
  }, [orderPayload, connectivityStatus, axiosCtx, closeModal]);

  return {
    loading,
    handleReprocessOrder,
  };
}

export default useOrderStatusModal;
